/* ============================================================
   EXON cashback rate reader (CB-S1a/S1b, EXO-5355/5356). The ONE client read
   of the shop's cashback rates document (shop metafield
   exon.cashback, CB-W1 v:1) once the theme switches to it at
   settings.cashback_cutover_at. snippets/cb-store-rate.liquid
   (`bps: true`) is its server twin and mirrors the same selection.

   Exposes on window.EXON (extended, never replaced):
     CB_LEGACY                      "LEGACY": before the cutover the
                                    legacy settings rate still rules;
     cbRateBps(handle, variantId, nowMs)
                                    the rate in basis points (0 valid),
                                    null = no chip, or CB_LEGACY;
     cbAmount(priceMinor, bps)      whole shekels, rounded UP; null on
                                    bad input, bps <= 0 or a non-finite
                                    result;
     cbNextChangeMs(nowMs)          the next instant (ms) after now at
                                    which cbRateBps can change (the
                                    cutover, a version's effective_from
                                    or ends_at), or null: a doc-mode
                                    page repaints there.

   CB-S1b (EXO-5356, ~/.claude/peers/threads/drop/drop-cb-s1b/
   CONTRACT.md §2) adds the chip and clock layer every cashback chip
   that is not the PDP row reads (cards.js paints, placements.js and
   product.js print):
     cbPctText(bps)                 THE percent formatter: "5", "2.5",
                                    "0.01" for an integer 0..10000,
                                    else "";
     cbLegacyPct(nowMs, giftCard)   the legacy store rate at now
                                    (cashbackPct, or cashbackLegacyNext
                                    once its switch has passed; a gift
                                    card is already at the after rate);
     cbLegacyNextMs(nowMs)          the pending legacy switch instant
                                    after now, or null;
     cbClock()                      { server, device }: the server
                                    render instant (cashbackNowMs)
                                    advanced on the monotonic clock,
                                    and Date.now();
     cbChipText(handle, variantId, giftCard, label)
                                    the chip text, "" (hidden) when the
                                    two legs of cbClock read different
                                    texts (§R4, generalised);
     cbChipNextDelayMs()            ms until either leg reaches the next
                                    instant a chip can change, or null;
     cbChipHTML(handle, variantId, giftCard, label)
                                    THE chip markup (CONTRACT.md §3) with
                                    its text from cbChipText, attributes
                                    escaped: placements.js and product.js
                                    print it, cards.js repaints it;
     cbLegacyBoth()                 true iff BOTH legs of cbClock are
                                    strictly before a valid
                                    cashbackCutoverAt (ADJUDICATION.md
                                    §A.1): the only state in which a
                                    legacy server-rendered chip or PDP
                                    row may show.

   The numbered steps below are CONTRACT.md §2 steps 1-6
   (~/.claude/peers/threads/drop/drop-cb-s1a/CONTRACT.md, amended by
   ADJUDICATION-1.md §A); snippets/cb-store-rate.liquid uses the same
   numbers.

   Fail-closed throughout: anything missing, malformed or ambiguous
   is null (no chip), never a guessed rate. Reads the config island
   #exon-placements-cfg at call time; no network, no dependencies.
   ============================================================ */
(function () {
  'use strict';
  var EXON = window.EXON = window.EXON || {};
  var LEGACY = 'LEGACY';
  /* the contract regex, with capture groups for the component checks */
  var INSTANT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?Z$/;
  var MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  var MAX_BPS = 10000;
  var DIGITS = /^\d+$/;
  var GID = /^gid:\/\/shopify\/ProductVariant\/(\d+)$/;
  var has = Object.prototype.hasOwnProperty;
  var toStr = Object.prototype.toString;

  /* the monotonic clock and its reading when this file evaluates (P0):
     cbClock's server leg = cashbackNowMs + (performance.now() - P0) */
  var PERF = (typeof performance !== 'undefined' && performance && typeof performance.now === 'function') ? performance : null;
  var P0 = PERF ? PERF.now() : NaN;

  /* parse cache keyed by the raw island text: a changed text re-parses */
  var cacheRaw = null, cacheCfg = null;

  function readCfg() {
    var el = document.getElementById('exon-placements-cfg');
    if (!el) return null;
    var raw = el.textContent;
    if (raw === cacheRaw) return cacheCfg;
    var cfg = null;
    try { cfg = JSON.parse(raw); } catch (e) { cfg = null; }
    if (!isPlain(cfg)) cfg = null;
    cacheRaw = raw;
    cacheCfg = cfg;
    return cfg;
  }

  function isPlain(v) {
    return v !== null && typeof v === 'object' && toStr.call(v) === '[object Object]';
  }

  /* a valid UTC instant string → epoch ms, else NaN. Valid = the regex
     AND month 01-12, day 01-(days in that month, Gregorian leap years),
     hour 00-23, minute 00-59, second 00-59 (ADJUDICATION-1 A.1): no
     roll-over of "05:00:60Z" or "02-30", the same rule as the Liquid
     twin. Built with setUTCFullYear, not Date.parse, so no engine's
     own leniency (or Safari's fraction strictness) decides validity. */
  function instant(s) {
    if (typeof s !== 'string') return NaN;
    var m = INSTANT.exec(s);
    if (!m) return NaN;
    var y = +m[1], mo = +m[2], d = +m[3], h = +m[4], mi = +m[5];
    var sec = m[6] === undefined ? 0 : +m[6];
    var ms = m[7] === undefined ? 0 : +(m[7] + '00').slice(0, 3);
    if (mo < 1 || mo > 12 || h > 23 || mi > 59 || sec > 59) return NaN;
    var leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
    var dim = (mo === 2 && leap) ? 29 : MONTH_DAYS[mo - 1];
    if (d < 1 || d > dim) return NaN;
    var t = new Date(0);
    t.setUTCFullYear(y, mo - 1, d);
    t.setUTCHours(h, mi, sec, ms);
    var v = t.getTime();
    return isFinite(v) ? v : NaN;
  }

  function isRate(r) {
    return typeof r === 'number' && isFinite(r) && Math.floor(r) === r && r >= 0 && r <= MAX_BPS;
  }

  /* variant id → its digit string, or null (UNKNOWN) */
  function variantKey(id) {
    if (typeof id === 'number') {
      return (isFinite(id) && Math.floor(id) === id && id >= 0) ? String(id) : null;
    }
    if (typeof id !== 'string') return null;
    if (DIGITS.test(id)) return id;
    var m = GID.exec(id);
    return m ? m[1] : null;
  }

  function pickVersion(versions, now) {
    var best = null, bestFrom = -Infinity;
    for (var i = 0; i < versions.length; i++) {
      var v = versions[i];
      if (!isPlain(v)) continue;
      var from = instant(v.effective_from);
      if (!isFinite(from) || from > now) continue;
      if (v.ends_at !== null) {
        var end = instant(v.ends_at);
        if (!isFinite(end) || !(now < end)) continue;
      }
      /* more than one covers: the latest effective_from wins (first on a tie) */
      if (from > bestFrom) { best = v; bestFrom = from; }
    }
    return best;
  }

  /* step 5: the rate for handle/variant in the picked version */
  function lookup(version, handle, variantId) {
    var byHandle = has.call(version, 'by_handle') ? version.by_handle : undefined;
    if (byHandle === undefined) byHandle = {};
    if (!isPlain(byHandle)) return null;
    if (typeof handle !== 'string' || handle === '') return null;
    var rate;
    if (!has.call(byHandle, handle)) {
      rate = version.default_bps;
    } else {
      var entry = byHandle[handle];
      if (typeof entry === 'number') {
        rate = entry;
      } else if (isPlain(entry)) {
        var key = variantKey(variantId);
        if (key === null) return null;
        if (has.call(entry, key)) rate = entry[key];
        else if (has.call(entry, '*')) rate = entry['*'];
        else rate = version.default_bps;
      } else {
        return null;
      }
    }
    return isRate(rate) ? rate : null;
  }

  function nowOr(nowMs) {
    return (typeof nowMs === 'number' && isFinite(nowMs)) ? nowMs : Date.now();
  }

  /* step 3: the v:1 document from the cfg, or null */
  function readDoc(cfg) {
    var doc = cfg.cashback;
    if (typeof doc === 'string') {
      try { doc = JSON.parse(doc); } catch (e) { return null; }
    }
    if (!isPlain(doc) || doc.v !== 1 || toStr.call(doc.versions) !== '[object Array]') return null;
    return doc;
  }

  function cbRateBps(handle, variantId, nowMs) {
    var now = nowOr(nowMs);
    var cfg = readCfg();
    if (!cfg) return null;
    /* step 1: the cutover */
    var cutover = instant(cfg.cashbackCutoverAt);
    if (!isFinite(cutover)) return null;
    /* step 2: before it the legacy settings rate still rules */
    if (now < cutover) return LEGACY;
    /* step 3: the document */
    var doc = readDoc(cfg);
    if (!doc) return null;
    /* step 4: the covering version */
    var version = pickVersion(doc.versions, now);
    if (!version) return null;
    /* steps 5-6: the rate (0 is a valid return) */
    return lookup(version, handle, variantId);
  }

  /* the smallest instant strictly after now among the valid cutover and
     every version's valid effective_from / non-null valid ends_at; null
     when there is none or the cfg is unreadable. An absent or unreadable
     document contributes no version instants but keeps a valid future
     cutover as a candidate (ADJUDICATION-1 A.2, round 1b). Pure: the same
     cfg read as cbRateBps. */
  function cbNextChangeMs(nowMs) {
    var now = nowOr(nowMs);
    var cfg = readCfg();
    if (!cfg) return null;
    var next = null;
    function consider(t) {
      if (isFinite(t) && t > now && (next === null || t < next)) next = t;
    }
    /* a readable cfg's valid future cutover counts even with no readable
       document; an absent/unreadable document adds no boundaries (A.2) */
    consider(instant(cfg.cashbackCutoverAt));
    var doc = readDoc(cfg);
    if (!doc) return next;
    for (var i = 0; i < doc.versions.length; i++) {
      var v = doc.versions[i];
      if (!isPlain(v)) continue;
      consider(instant(v.effective_from));
      if (v.ends_at !== null) consider(instant(v.ends_at));
    }
    return next;
  }

  function cbAmount(priceMinor, bps) {
    if (typeof priceMinor !== 'number' || !isFinite(priceMinor) || priceMinor < 0) return null;
    if (typeof bps !== 'number' || !isFinite(bps) || !(bps > 0)) return null;
    var amount = Math.ceil(priceMinor * bps / 1000000);
    return isFinite(amount) ? amount : null;
  }

  /* ---------------- CB-S1b: chips and the two-instant clock ---------------- */

  function cbPctText(bps) {
    return isRate(bps) ? String(bps / 100) : '';
  }

  function isNum(v) { return typeof v === 'number' && isFinite(v); }

  /* the pending (or passed) legacy store switch from the cfg, or null:
     a plain object whose at is a finite number > 0 and pct a finite
     number >= 0 (layout/theme.liquid prints it while sr_switch_s > 0) */
  function legacyNext(cfg) {
    var n = cfg ? cfg.cashbackLegacyNext : null;
    if (!isPlain(n) || !isNum(n.at) || !(n.at > 0) || !isNum(n.pct) || !(n.pct >= 0)) return null;
    return n;
  }

  /* the legacy (settings) store rate at now: the after-switch rate once
     the switch has passed; a gift card is already at the after-switch
     rate while it is pending (Weekend sale 2026-10-09, cb-store-rate
     `gift_card`); else cashbackPct (0 when absent or invalid) */
  function cbLegacyPct(nowMs, giftCard) {
    var now = nowOr(nowMs);
    var cfg = readCfg();
    var base = (cfg && isNum(cfg.cashbackPct) && cfg.cashbackPct >= 0) ? cfg.cashbackPct : 0;
    var next = legacyNext(cfg);
    if (next && now >= next.at) return next.pct;
    if (next && giftCard === true) return next.pct;
    return base;
  }

  function cbLegacyNextMs(nowMs) {
    var now = nowOr(nowMs);
    var next = legacyNext(readCfg());
    return (next && next.at > now) ? next.at : null;
  }

  /* the two instants (§R4.1 for every chip): device = Date.now(); server =
     the cfg's cashbackNowMs advanced by the monotonic time since this file
     evaluated, so a device-clock correction after boot does not move it;
     device when cashbackNowMs is not a finite number or there is no
     performance.now. Re-read at every call. */
  function cbClock() {
    var device = Date.now();
    var cfg = readCfg();
    var nowMs = cfg ? cfg.cashbackNowMs : undefined;
    var server = (PERF && isFinite(P0) && isNum(nowMs))
      ? nowMs + (PERF.now() - P0)
      : device;
    return { server: server, device: device };
  }

  /* the chip text on one instant: LEGACY → the legacy store rate, a
     document rate > 0 → its percent, anything else (null, 0) → "" */
  function chipTextAt(handle, variantId, giftCard, label, now) {
    if (typeof label !== 'string') return '';
    var r = cbRateBps(handle, variantId, now);
    if (r === LEGACY) {
      var p = cbLegacyPct(now, giftCard);
      return p > 0 ? p + '% ' + label : '';
    }
    if (typeof r === 'number' && r > 0) return cbPctText(r) + '% ' + label;
    return '';
  }

  /* shown only when the server instant and the device instant read the
     same text; a straddled boundary (the cutover, a version edge, a legacy
     switch) cannot tell which value is in force and hides (fail-closed) */
  function cbChipText(handle, variantId, giftCard, label) {
    var t = cbClock();
    var a = chipTextAt(handle, variantId, giftCard, label, t.server);
    if (t.device === t.server) return a;
    var b = chipTextAt(handle, variantId, giftCard, label, t.device);
    return a === b ? a : '';
  }

  /* ms until the first leg reaches the next instant any chip can change
     (a document boundary or the pending legacy switch), measured on that
     leg; rounded up so the leg that set it has crossed when the timer
     runs, capped at setTimeout's maximum (§R4.3); null = nothing pending */
  function cbChipNextDelayMs() {
    var t = cbClock();
    var d = null;
    function consider(next, leg) {
      if (isNum(next) && (d === null || next - leg < d)) d = next - leg;
    }
    consider(cbNextChangeMs(t.server), t.server);
    consider(cbLegacyNextMs(t.server), t.server);
    consider(cbNextChangeMs(t.device), t.device);
    consider(cbLegacyNextMs(t.device), t.device);
    if (d === null) return null;
    return Math.min(Math.max(Math.ceil(d), 0), 2147483647);
  }

  /* text or attribute value → HTML-safe */
  function esc(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* THE chip (CONTRACT.md §3), printed by every JS producer: always
     emitted, [hidden] while its text is "", so cards.js can show it later.
     The text comes through the published EXON.cbChipText (§3: without it
     the chip is hidden and empty), so there is one seam to stub or replace.
     snippets/product-card.liquid prints the same span (hidden, empty) in
     DOC phase; it is the one other copy, since Liquid cannot call this. */
  function cbChipHTML(handle, variantId, giftCard, label) {
    var h = handle == null ? '' : String(handle);
    var v = variantId == null ? '' : String(variantId);
    var l = typeof label === 'string' ? label : '';
    var gift = giftCard === true;
    var E = window.EXON;
    var text = E && typeof E.cbChipText === 'function' ? E.cbChipText(h, v, gift, l) : '';
    if (typeof text !== 'string') text = '';
    return '<span class="cb-mini" data-cb-chip data-cb-handle="' + esc(h) + '" data-cb-variant="' + esc(v) +
      '" data-cb-label="' + esc(l) + '"' + (gift ? ' data-cb-gift="1"' : '') + (text ? '' : ' hidden') + '>' +
      esc(text) + '</span>';
  }

  /* ADJUDICATION.md §A.1: a legacy surface (an SSR card chip without
     data-cb-chip, the PDP row without data-cb-mode="doc") is shown only
     while both legs are strictly before a valid cutover. An absent or
     invalid cutover, no cfg, or either leg at or past it → false (hide). */
  function cbLegacyBoth() {
    var cfg = readCfg();
    if (!cfg) return false;
    var cutover = instant(cfg.cashbackCutoverAt);
    if (!isFinite(cutover)) return false;
    var t = cbClock();
    return t.server < cutover && t.device < cutover;
  }

  EXON.CB_LEGACY = LEGACY;
  EXON.cbRateBps = cbRateBps;
  EXON.cbAmount = cbAmount;
  EXON.cbNextChangeMs = cbNextChangeMs;
  EXON.cbPctText = cbPctText;
  EXON.cbLegacyPct = cbLegacyPct;
  EXON.cbLegacyNextMs = cbLegacyNextMs;
  EXON.cbClock = cbClock;
  EXON.cbChipText = cbChipText;
  EXON.cbChipNextDelayMs = cbChipNextDelayMs;
  EXON.cbChipHTML = cbChipHTML;
  EXON.cbLegacyBoth = cbLegacyBoth;
})();
